<template>
    <nav class="menuv2" :class="{ 'menuv2--collapsed': isSidebarCollapsed }">
        <CompanySwitcher v-if="isSuperAdmin" :collapsed="isSidebarCollapsed" />
        <template v-for="(item, i) in visibleItems">
            <!-- Section title -->
            <div
                v-if="item.type === 'title'"
                :key="'t-' + i"
                v-show="!isSidebarCollapsed"
                class="menuv2-title"
            >
                {{ $t(item.label) }}
            </div>

            <!-- Simple link -->
            <router-link
                v-else-if="item.type === 'link'"
                :key="'l-' + i"
                :to="item.to"
                :exact="!!item.exact"
                class="menuv2-item"
                active-class="menuv2-item--active"
                :data-tip="$t(item.label)"
            >
                <i :class="item.icon" class="menuv2-item-icon"></i>
                <span v-show="!isSidebarCollapsed" class="menuv2-item-label">{{ $t(item.label) }}</span>
            </router-link>

            <!-- Group with children -->
            <div
                v-else-if="item.type === 'group'"
                :key="'g-' + i"
                class="menuv2-group"
                :class="{
                    'menuv2-group--open': isGroupOpen(i),
                    'menuv2-group--flyout-open': flyoutOpenFor === i
                }"
                @mouseenter="onGroupEnter(i)"
                @mouseleave="onGroupLeave(i)"
            >
                <button
                    type="button"
                    class="menuv2-item menuv2-group-toggle"
                    :class="{ 'menuv2-item--active': isGroupActive(item) }"
                    :data-tip="$t(item.label)"
                    @click="toggleGroup(i)"
                >
                    <i :class="item.icon" class="menuv2-item-icon"></i>
                    <span v-show="!isSidebarCollapsed" class="menuv2-item-label">{{ $t(item.label) }}</span>
                    <i
                        v-show="!isSidebarCollapsed"
                        class="fal fa-chevron-down menuv2-group-chevron"
                    ></i>
                </button>
                <transition
                    name="menuv2-slide"
                    @before-enter="onBeforeEnter"
                    @enter="onEnter"
                    @before-leave="onBeforeLeave"
                    @leave="onLeave"
                >
                    <div
                        v-if="!isSidebarCollapsed && isGroupOpen(i)"
                        class="menuv2-group-children"
                    >
                        <router-link
                            v-for="(child, j) in visibleChildren(item)"
                            :key="'c-' + i + '-' + j"
                            :to="child.to"
                            :exact="!!child.exact"
                            class="menuv2-subitem"
                            :class="{ 'menuv2-subitem--active': routeMatches(child) }"
                            :title="$t(child.label)"
                        >
                            {{ $t(child.label) }}
                        </router-link>
                    </div>
                </transition>

                <!-- Collapsed-mode flyout: children shown on hover -->
                <div v-if="isSidebarCollapsed" class="menuv2-flyout" role="menu">
                    <div class="menuv2-flyout-title">{{ $t(item.label) }}</div>
                    <router-link
                        v-for="(child, j) in visibleChildren(item)"
                        :key="'fc-' + i + '-' + j"
                        :to="child.to"
                        :exact="!!child.exact"
                        class="menuv2-flyout-item"
                        :class="{ 'menuv2-flyout-item--active': routeMatches(child) }"
                        @click.native="flyoutOpenFor = null"
                    >
                        {{ $t(child.label) }}
                    </router-link>
                </div>
            </div>
        </template>
    </nav>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CompanySwitcher from './CompanySwitcher.vue'

// Static navigation definition. Dynamic non-listable form types are merged in `visibleItems`.
const NAV = [
    { type: 'link', label: 'menu.dashboard', icon: 'fal fa-th', to: '/', exact: true },
    {
        type: 'group',
        label: 'menu.tickets.ticket',
        icon: 'fal fa-ticket',
        permission: 'posts.view',
        key: 'tickets',
        children: [
            { type: 'link', label: 'menu.tickets.all', to: '/posts', exact: true },
            { type: 'link', label: 'menu.tickets.new', to: '/posts?status=new' },
            { type: 'link', label: 'menu.tickets.open', to: '/posts?status=open' },
            { type: 'link', label: 'menu.tickets.my', to: '/posts?responsability=operator' },
            { type: 'link', label: 'menu.tickets.create', to: '/post/create/forms', permission: 'posts.create' }
        ]
    },
    {
        type: 'group',
        label: 'menu.forms.form',
        icon: 'fal fa-file-alt',
        permission: 'forms.view',
        key: 'forms',
        children: [
            { type: 'link', label: 'menu.forms.all_forms', to: '/forms', exact: true },
            { type: 'link', label: 'menu.forms.create', to: '/forms/builder', permission: 'forms.create' },
            { type: 'link', label: 'menu.forms.form_types', to: '/form-types', permission: 'form-types.view' }
        ]
    },
    {
        type: 'group',
        label: 'menu.statistics.statistic',
        icon: 'fal fa-chart-line',
        permission: 'company.statistics',
        key: 'stats',
        children: [
            { type: 'link', label: 'menu.statistics.dashboard', to: '/statistiques' },
            { type: 'link', label: 'menu.statistics.performance', to: '/performances' }
        ]
    },
    { type: 'link', label: 'menu.map', icon: 'fal fa-map-marker-alt', to: '/map', permission: 'posts.view' },

    {
        type: 'title',
        label: 'menu.parameters.name',
        anyPermission: ['users.view', 'company.settings', 'forms.view']
    },
    {
        type: 'group',
        label: 'menu.companies.company',
        icon: 'fal fa-building',
        anyPermission: ['users.view', 'company.settings'],
        key: 'company',
        children: [
            { type: 'link', label: 'menu.companies.back_office', to: '/company/users', permission: 'users.view' },
            { type: 'link', label: 'menu.companies.settings', to: '/company/settings', permission: 'company.settings' },
            { type: 'link', label: 'menu.companies.webhooks', to: '/company/webhooks', permission: 'company.settings' },
            { type: 'link', label: 'menu.companies.permissions', to: '/company/permissions', permission: 'company.settings' },
            { type: 'link', label: 'menu.companies.backoffice_infos', to: '/company/backoffice-infos', permission: 'company.settings' }
        ]
    },
    { type: 'link', label: 'menu.templates', icon: 'fal fa-envelope', to: '/admin/templates', permission: 'response-templates.view' },
    {
        type: 'group',
        label: 'menu.news.news',
        icon: 'fal fa-newspaper',
        anyPermission: ['news.view', 'news-categories.view'],
        key: 'news',
        children: [
            { type: 'link', label: 'menu.news.list', to: '/news', permission: 'news.view' },
            { type: 'link', label: 'menu.news.categories', to: '/news/categories', permission: 'news-categories.view' }
        ]
    },

    {
        type: 'group',
        label: 'menu.events.events',
        icon: 'fal fa-calendar-star',
        anyPermission: ['events.view'],
        key: 'events',
        children: [
            { type: 'link', label: 'menu.events.list', to: '/events', permission: 'events.view' },
            { type: 'link', label: 'menu.events.create', to: '/events/new', permission: 'events.create' }
        ]
    },

    { type: 'title', label: 'menu.superadmin.title', superAdminOnly: true },
    { type: 'link', label: 'menu.superadmin.companies', icon: 'fal fa-building', to: '/admin/companies', exact: false, superAdminOnly: true },
    { type: 'link', label: 'menu.superadmin.news', icon: 'fal fa-newspaper', to: '/admin/news', exact: false, superAdminOnly: true }
]

export default {
    name: 'MenuV2',
    components: { CompanySwitcher },
    data() {
        return {
            openGroup: null,
            flyoutOpenFor: null,
            nonListableTypes: []
        }
    },
    computed: {
        ...mapState('ui', ['isSidebarCollapsed']),
        permissions() {
            return this.$store.state.auth.permissions
                || JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        isSuperAdmin() {
            const role = this.$store.state.auth.role
                || JSON.parse(localStorage.getItem('role') || 'null')
            const roleName = role && typeof role === 'object' ? role.name : role
            return roleName === 'super-admin'
        },
        visibleItems() {
            const items = this.expandNav([...NAV])
            // Insert non-listable form types right after the Tickets group.
            if (this.nonListableTypes.length) {
                const ticketsIdx = items.findIndex((i) => i.key === 'tickets')
                if (ticketsIdx !== -1) {
                    const dynGroups = this.nonListableTypes.map((t) => ({
                        type: 'group',
                        label: t.name,
                        rawLabel: true,
                        icon: 'fal fa-layer-group',
                        key: 'ft-' + t.id,
                        children: [
                            { type: 'link', label: 'menu.tickets.all', to: `/posts/type/${t.id}`, exact: true }
                        ]
                    }))
                    items.splice(ticketsIdx + 1, 0, ...dynGroups)
                }
            }
            return items.filter((item) => this.itemAllowed(item))
        }
    },
    created() {
        this.loadNonListableTypes()
        this.restoreOpenGroups()
        this.autoOpenActiveGroups()
    },
    watch: {
        '$route'() {
            this.autoOpenActiveGroups()
        }
    },
    methods: {
        ...mapActions('form', ['getFormTypes']),
        itemAllowed(item) {
            if (item.superAdminOnly && !this.isSuperAdmin) return false
            if (item.permission && !this.permissions.includes(item.permission)) return false
            if (item.anyPermission && !item.anyPermission.some((p) => this.permissions.includes(p))) return false
            return true
        },
        visibleChildren(group) {
            return (group.children || []).filter((c) => this.itemAllowed(c))
        },
        expandNav(list) {
            return list.map((item) => {
                if (item.rawLabel) return item
                return item
            })
        },
        isGroupOpen(index) {
            return this.openGroup === index
        },
        isGroupActive(group) {
            if (!group.children) return false
            return group.children.some((c) => c.to && this.routeMatches(c))
        },
        routeMatches(child) {
            if (!child.to) return false
            const to = String(child.to)
            const [toPath, toQuery] = to.split('?')
            if (this.$route.path !== toPath) return false
            if (!toQuery) {
                // Only match when current URL has no query of its own, so /posts
                // doesn't steal the active state from /posts?status=new.
                const currentQueryKeys = Object.keys(this.$route.query || {})
                return currentQueryKeys.length === 0
            }
            const params = new URLSearchParams(toQuery)
            for (const [k, v] of params) {
                if (this.$route.query[k] !== v) return false
            }
            return true
        },
        toggleGroup(index) {
            this.openGroup = this.openGroup === index ? null : index
            this.persistOpenGroup()
        },
        onGroupEnter(index) {
            if (this.isSidebarCollapsed) this.flyoutOpenFor = index
        },
        onGroupLeave() {
            if (this.isSidebarCollapsed) this.flyoutOpenFor = null
        },
        autoOpenActiveGroups() {
            const activeIdx = this.visibleItems.findIndex(
                (item) => item.type === 'group' && this.isGroupActive(item)
            )
            if (activeIdx !== -1) {
                this.openGroup = activeIdx
                this.persistOpenGroup()
            }
        },
        persistOpenGroup() {
            try {
                localStorage.setItem('ui.menuV2.openGroup', this.openGroup === null ? '' : String(this.openGroup))
            } catch (e) { /* ignore */ }
        },
        restoreOpenGroups() {
            try {
                const raw = localStorage.getItem('ui.menuV2.openGroup')
                if (raw !== null && raw !== '') this.openGroup = parseInt(raw, 10)
            } catch (e) { /* ignore */ }
        },
        onBeforeEnter(el) {
            el.style.height = '0'
            el.style.opacity = '0'
            el.style.overflow = 'hidden'
        },
        onEnter(el, done) {
            void el.offsetHeight
            el.style.transition = 'height 180ms ease, opacity 180ms ease'
            el.style.height = el.scrollHeight + 'px'
            el.style.opacity = '1'
            const handler = () => {
                el.removeEventListener('transitionend', handler)
                el.style.height = 'auto'
                el.style.transition = ''
                el.style.overflow = ''
                done()
            }
            el.addEventListener('transitionend', handler, { once: true })
        },
        onBeforeLeave(el) {
            el.style.height = el.scrollHeight + 'px'
            el.style.opacity = '1'
            el.style.overflow = 'hidden'
        },
        onLeave(el, done) {
            void el.offsetHeight
            el.style.transition = 'height 160ms ease, opacity 160ms ease'
            el.style.height = '0'
            el.style.opacity = '0'
            const handler = () => {
                el.removeEventListener('transitionend', handler)
                el.style.transition = ''
                done()
            }
            el.addEventListener('transitionend', handler, { once: true })
        },
        loadNonListableTypes() {
            if (!this.permissions.includes('posts.view')) return
            if (!this.permissions.includes('form-types.view')) return
            this.getFormTypes({ silence403: true })
                .then((response) => {
                    const types = (response && response.data && (response.data.types || response.data)) || []
                    this.nonListableTypes = types.filter((t) => t.active && !t.listable)
                })
                .catch(() => {})
        }
    }
}
</script>

<style scoped>
.menuv2 {
    padding: var(--v2-space-3) var(--v2-space-2);
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.menuv2-title {
    font-size: 0.6875rem;
    color: var(--v2-text-4);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: var(--v2-font-weight-semibold);
    padding: var(--v2-space-4) var(--v2-space-3) var(--v2-space-2);
}

.menuv2-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    width: 100%;
    height: 32px;
    padding: 0 var(--v2-space-3);
    border-radius: var(--v2-radius-sm);
    color: var(--v2-text-2);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-medium);
    text-decoration: none;
    cursor: pointer;
    background: transparent;
    border: none;
    text-align: left;
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
    font-family: var(--v2-font-sans);
    box-sizing: border-box;
}

.menuv2-item:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.menuv2-item:focus-visible {
    outline: none;
    box-shadow: var(--v2-shadow-focus);
}

.menuv2-item--active,
.menuv2-item--active:hover {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-weight: var(--v2-font-weight-semibold);
}

.menuv2-item--active::before {
    content: '';
    position: absolute;
    left: -2px;
    top: 6px;
    bottom: 6px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: var(--v2-primary);
}

.menuv2-item-icon {
    font-size: 1rem;
    width: 18px;
    flex-shrink: 0;
    text-align: center;
}

.menuv2-item-label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Group */
.menuv2-group {
    display: flex;
    flex-direction: column;
}

.menuv2-group-toggle {
    width: 100%;
}

.menuv2-group-chevron {
    font-size: 0.7rem;
    color: var(--v2-text-4);
    transition: transform var(--v2-transition-base), color var(--v2-transition-fast);
}

.menuv2-group--open > .menuv2-group-toggle .menuv2-group-chevron {
    color: var(--v2-text-2);
    transform: rotate(-180deg);
}

.menuv2-group-children {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 2px 0 2px calc(var(--v2-space-3) + 18px + var(--v2-space-3));
}

.menuv2-subitem {
    display: block;
    padding: 6px var(--v2-space-3);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-regular);
    color: var(--v2-text-3);
    border-radius: var(--v2-radius-sm);
    text-decoration: none;
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
}

.menuv2-subitem:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.menuv2-subitem--active,
.menuv2-subitem--active:hover {
    color: var(--v2-primary);
    font-weight: var(--v2-font-weight-medium);
    background: var(--v2-primary-soft);
}

/* Collapsed state */
.menuv2--collapsed .menuv2-item {
    justify-content: center;
    padding: 0;
}

.menuv2--collapsed .menuv2-item-icon {
    width: auto;
    font-size: 1.05rem;
}

.menuv2--collapsed .menuv2-group-children {
    display: none;
}

.menuv2--collapsed .menuv2-item--active::before {
    left: 4px;
}

/* Hide the tip tooltip on groups in collapsed mode — the flyout covers it */
.menuv2--collapsed .menuv2-group .menuv2-group-toggle[data-tip]::after {
    display: none;
}

/* Collapsed flyout panel */
.menuv2-group {
    position: relative;
}

.menuv2-flyout {
    display: none;
    position: absolute;
    left: calc(100% + 10px);
    top: 0;
    min-width: 200px;
    padding: 4px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-lg);
    z-index: 1010;
    opacity: 0;
    visibility: hidden;
    transform: translateX(-4px);
    pointer-events: none;
    transition: opacity var(--v2-transition-fast),
        transform var(--v2-transition-fast),
        visibility 0s linear var(--v2-transition-fast);
}

/* Invisible hover bridge so moving the cursor from the button to the
 * flyout doesn't leave the :hover area during the 10px gap. */
.menuv2-flyout::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -12px;
    width: 12px;
}

.menuv2--collapsed .menuv2-flyout {
    display: block;
}

.menuv2--collapsed .menuv2-group--flyout-open .menuv2-flyout {
    opacity: 1;
    visibility: visible;
    transform: translateX(0);
    pointer-events: auto;
    transition-delay: 120ms, 120ms, 0s;
}

.menuv2-flyout-title {
    padding: 6px var(--v2-space-3) 4px;
    font-size: 0.68rem;
    color: var(--v2-text-4);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: var(--v2-font-weight-semibold);
}

.menuv2-flyout-item {
    display: block;
    padding: 7px var(--v2-space-3);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-2);
    border-radius: var(--v2-radius-sm);
    text-decoration: none;
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
}

.menuv2-flyout-item:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.menuv2-flyout-item--active,
.menuv2-flyout-item--active:hover {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-weight: var(--v2-font-weight-medium);
}

/* Custom tooltip visible only when sidebar is collapsed */
.menuv2--collapsed .menuv2-item {
    position: relative;
}

.menuv2--collapsed .menuv2-item[data-tip]::after {
    content: attr(data-tip);
    position: absolute;
    left: calc(100% + 12px);
    top: 50%;
    transform: translateY(-50%) translateX(-4px);
    padding: 4px 8px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-text-1);
    color: var(--v2-surface-0);
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-medium);
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity var(--v2-transition-fast),
        transform var(--v2-transition-fast),
        visibility 0s linear var(--v2-transition-fast);
    z-index: 1000;
    box-shadow: var(--v2-shadow-md);
}

.menuv2--collapsed .menuv2-item[data-tip]:hover::after,
.menuv2--collapsed .menuv2-item[data-tip]:focus-visible::after {
    opacity: 1;
    visibility: visible;
    transform: translateY(-50%) translateX(0);
    transition-delay: 150ms, 150ms, 0s;
}
</style>
